import React, { Fragment, useState, useEffect } from "react"
import {
  Container,
  Col,
  Row,
  Button,
  Card,
  CardBody,
  Input,
} from "reactstrap"
import { useMutation } from "react-query"
import { GENERAL } from "../../api/general"
import { useParams, useLocation } from "react-router-dom"
//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import Swal from "sweetalert2"
import PaginatedDataTable from "components/PaginatedDataTable"

const NationalTargetsLga = props => {
  const { stateId } = useParams()
  const location = useLocation()
  const stateName = location.state?.stateName || "State"

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [data, setData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [filter, setFilter] = useState("")
  const [editingRow, setEditingRow] = useState(null)
  const [editingValue, setEditingValue] = useState("")

  const fetchRecords = async (page, pageSize, filter) => {
    Swal.fire({
      title: "Fetching records...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading(Swal.getDenyButton())
      },
    })
    try {
      const response = await getNationalTargetsByState.mutateAsync({
        stateId,
        page,
        pageSize,
        searchKey: filter ?? "",
      })
      Swal.close()
      setData(response?.palliative || [])
      setTotalRecords(response?.totalRecords || 0)
    } catch (error) {
      Swal.fire("Error fetching records", error.message, "error")
    }
  }

  useEffect(() => {
    fetchRecords(page, pageSize, filter)
  }, [page, pageSize])

  const getNationalTargetsByState = useMutation({
    mutationFn: ({ stateId, page, pageSize, searchKey }) =>
      GENERAL.getNationalTargetsByState({ stateId, page, pageSize, searchKey }),
    onError: error => Swal.fire("Error", error.message, "error"),
  })

  const setNationalTargetLga = useMutation({
    mutationFn: payload => GENERAL.setNationalTargetLga(payload),
    onSuccess: () => {
      Swal.fire("Success", "LGA target updated successfully.", "success").then(
        () => fetchRecords(page, pageSize, filter)
      )
      setEditingRow(null)
    },
    onError: error => Swal.fire("Error", error.message, "error"),
  })

  const handleSaveTarget = row => {
    if (!editingValue && editingValue !== 0) {
      return Swal.fire("Validation", "Target value is required.", "warning")
    }
    Swal.fire({
      title: "Saving target...",
      showConfirmButton: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(Swal.getDenyButton()),
    })
    setNationalTargetLga.mutate({ lgaId: row.id, target: editingValue })
  }

  const handlePageChange = page => setPage(page)
  const handlePerRowsChange = newPageSize => {
    setPageSize(newPageSize)
    setPage(1)
  }
  const handleSearchFn = () => {
    setPage(1)
    fetchRecords(1, pageSize, filter.trim())
  }

  const startIndex = (page - 1) * pageSize

  const columns = [
    {
      name: "#",
      selector: (row, index) => startIndex + index + 1,
      width: "4rem",
      sortable: true,
    },
    {
      name: "LGA",
      sortable: true,
      selector: row => row.name,
    },
    {
      name: "Current Target",
      sortable: true,
      selector: row =>
        editingRow === row.id ? (
          <Input
            type="number"
            min={0}
            value={editingValue}
            onChange={e => setEditingValue(e.target.value)}
            style={{ width: 120 }}
          />
        ) : (
          <span>{row.target ?? "—"}</span>
        ),
    },
    {
      name: "Actual Registrations",
      sortable: true,
      selector: row => row.actual ?? 0,
    },
    {
      name: "% Completion",
      sortable: true,
      selector: row =>
        row.target
          ? `${Math.round(((row.actual ?? 0) / row.target) * 100)}%`
          : "—",
    },
    {
      name: "Action",
      sortable: false,
      selector: row => (
        <div style={{ display: "flex", gap: 6 }}>
          {editingRow === row.id ? (
            <>
              <Button color="success" size="sm" onClick={() => handleSaveTarget(row)}>
                Save
              </Button>
              <Button color="secondary" size="sm" onClick={() => setEditingRow(null)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button
              color="primary"
              size="sm"
              onClick={() => {
                setEditingRow(row.id)
                setEditingValue(row.target ?? "")
              }}
            >
              Set Target
            </Button>
          )}
        </div>
      ),
    },
  ]

  document.title = `${stateName} LGA Targets | UNICEF - e-Recruitment Portal`

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid className="container-xxl" style={{ minWidth: "97%" }}>
          <Row>
            <Breadcrumbs
              title={props.t("National Targets")}
              breadcrumbItem={props.t(`${stateName} — LGA Targets`)}
              hasBackBtn={true}
            />
          </Row>
          <Card>
            <CardBody>
              <div className="mb-4 h4 card-title">
                LGA Registration Targets — {stateName}
              </div>
              <Fragment>
                <Row className="mb-2">
                  <Col md={10}>
                    <div className="search-box me-xxl-2 my-3 my-xxl-0 d-inline-block">
                      <div
                        className="position-relative"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <label htmlFor="search-bar-lga" className="search-label">
                          <span id="search-bar-lga-label" className="sr-only">
                            Search this table
                          </span>
                          <input
                            onChange={e => setFilter(e.target.value)}
                            id="search-bar-lga"
                            type="text"
                            className="form-control"
                            placeholder="Search LGAs..."
                            value={filter || ""}
                          />
                        </label>
                        <i
                          className="bx bx-search-alt search-icon"
                          style={{ cursor: "pointer" }}
                          onClick={handleSearchFn}
                        ></i>
                        <div
                          className="d-inline-block"
                          style={{ marginLeft: 5, marginBottom: 8 }}
                        >
                          <Button
                            type="button"
                            color="primary"
                            className="btn-md btn-rounded"
                            onClick={handleSearchFn}
                          >
                            Search
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
                <PaginatedDataTable
                  columns={columns}
                  data={data}
                  totalRecords={totalRecords}
                  handlePageChange={handlePageChange}
                  handlePerRowsChange={handlePerRowsChange}
                />
              </Fragment>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withTranslation()(NationalTargetsLga)
